// EL BANCO DE MUESTRAS: lo que carga y reproduce los ficheros de audio de la casa.
//
// QUÉ ES Y QUÉ NO. El catálogo —qué sonido tiene fichero, en qué canal y a qué volumen— está en
// `muestras.mjs` y se comprueba con `node`. Esto es la máquina: pide el fichero, lo decodifica una
// vez, lo guarda y lo dispara. No decide nada.
//
// LA REGLA QUE GOBIERNA TODO ESTE FICHERO: **la casa nunca se queda muda**. Si el navegador no
// entiende ninguno de los dos formatos, si el fichero no está, si la red tarda o si la muestra aún
// no ha llegado cuando hace falta, `suena()` devuelve `false` y quien llamó sintetiza como ha hecho
// siempre. Un sonido peor es infinitamente mejor que un silencio que nadie sabe explicar.
//
// Y POR ESO ES SÍNCRONO. Lo natural habría sido `await carga(nombre)` y disparar al llegar, pero
// entonces el golpe suena cuando llega el fichero —medio segundo tarde, con el puño ya bajado— y
// eso es peor que no sonar. Aquí se mira lo que hay en memoria: si está, suena; si no, se pide para
// la próxima y se contesta que no, en el mismo fotograma.
//
// EL CONTEXTO ES EL DE LA CASA (`audioDeLaCasa.mjs`) y no uno nuevo: abrir el sexto `AudioContext`
// de la página fue exactamente la avería del Chromebook del 25 de septiembre.

import { contexto } from "./audioDeLaCasa.mjs";
import {
  LAS_DEL_COMBATE, MUESTRAS, canalDeLaMuestra, hayMuestra, rutaDe, tipoQueSuena,
} from "./muestras.mjs";

/** Cuánto se mueve el tono de una muestra que se repite, arriba y abajo. */
export const VARIA = 0.045;

let ext;                            // el formato elegido, una sola vez
let preguntado = false;
const cargadas = new Map();         // nombre -> AudioBuffer
const pidiendo = new Map();         // nombre -> promesa en vuelo, para no pedir dos veces
const rotas = new Set();            // las que fallaron: no se vuelven a pedir nunca

/**
 * QUÉ FORMATO ENTIENDE ESTE NAVEGADOR, preguntado una vez y guardado. `canPlayType` dice que no
 * devolviendo la cadena vacía, que es la respuesta que se olvida todo el mundo.
 */
export function formato(ventana = typeof window === "undefined" ? null : window) {
  if (preguntado) return ext;
  preguntado = true;
  ext = null;
  try {
    const a = ventana && ventana.document && ventana.document.createElement("audio");
    if (a && a.canPlayType) ext = tipoQueSuena((t) => a.canPlayType(t));
  } catch { ext = null; }
  return ext;
}

/** Si este sonido puede sonar con fichero: lo tiene, el navegador puede y no se ha roto antes. */
export const tiene = (nombre) => hayMuestra(nombre) && !rotas.has(nombre) && formato() !== null;

/** Si ya está en memoria y lista para disparar en este mismo fotograma. */
export const lista = (nombre) => cargadas.has(nombre);

/**
 * Trae una muestra y la decodifica. Si falla —404, red, un fichero corrupto— se apunta en `rotas`
 * y no se vuelve a intentar: reintentar un 404 en cada golpe de un combate son cuarenta peticiones
 * que fallan igual.
 */
export function carga(nombre) {
  if (!tiene(nombre)) return Promise.resolve(null);
  if (cargadas.has(nombre)) return Promise.resolve(cargadas.get(nombre));
  if (pidiendo.has(nombre)) return pidiendo.get(nombre);
  const ctx = contexto();
  if (!ctx) return Promise.resolve(null);
  const p = fetch(rutaDe(nombre, formato()))
    .then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.arrayBuffer();
    })
    .then((bytes) => ctx.decodeAudioData(bytes))
    .then((buf) => {
      cargadas.set(nombre, buf);
      pidiendo.delete(nombre);
      return buf;
    })
    .catch(() => {
      rotas.add(nombre);
      pidiendo.delete(nombre);
      return null;
    });
  pidiendo.set(nombre, p);
  return p;
}

/**
 * Las que hacen falta dentro de un momento. Se llama al entrar en el combate, **mientras corre el
 * cartel de la entrada**: dos segundos y medio en los que no suena casi nada y la red está libre.
 * Es el mismo hueco que el cartel le deja a three para montar la escena.
 */
export function precarga(nombres = LAS_DEL_COMBATE) {
  for (const n of nombres) carga(n);
}

/**
 * DISPARA UNA MUESTRA. Devuelve `true` si ha sonado —o si el canal estaba apagado, que también es
 * atenderla— y `false` si quien llamó tiene que sintetizar.
 *
 * `estado` son los canales encendidos (`canalesDe`, en `canalesDeSonido.mjs`). Si no se pasa, no se
 * filtra: quien llama ya lo ha mirado, que es lo que hace `sound.js`.
 */
export function suena(nombre, { estado = null, volumen = 1 } = {}) {
  if (!tiene(nombre)) return false;
  if (estado && estado[canalDeLaMuestra(nombre)] === false) return true;
  const buf = cargadas.get(nombre);
  if (!buf) { carga(nombre); return false; }
  const ctx = contexto();
  if (!ctx) return false;
  try {
    const fuente = ctx.createBufferSource();
    fuente.buffer = buf;
    // EL TONO SE MUEVE UN POCO EN CADA DISPARO, **Y SÓLO EN LAS QUE LO PIDEN**. Un golpe idéntico
    // cuarenta veces seguidas deja de oírse a la tercera —el oído lo da por sabido y lo tapa—, y
    // un cuatro y medio por ciento arriba o abajo basta para que siga llegando.
    //
    // PERO SON 76 CENTS, tres cuartos de semitono, y eso en algo tonal no es variación: es
    // desafinar. Hasta el 17 de octubre de 2026 esto lo llevaba un parámetro con `true` por
    // defecto que **no apagaba nadie**, así que salía movido todo —el acierto del repaso, la
    // fanfarria, el jaleo— y Gabri lo oyó: *«la segunda vez suena más desafinado y más grave»*.
    // Ahora lo declara cada muestra en `muestras.mjs`, que es donde ya viven su canal y su
    // volumen, y el que no lo diga no varía. Quien dispara un acierto no tiene por qué saber
    // que existe una ruleta de afinación.
    if (MUESTRAS[nombre].varia) fuente.playbackRate.value = 1 + (Math.random() - 0.5) * 2 * VARIA;
    const g = ctx.createGain();
    g.gain.value = Math.max(0, Math.min(1.2, (MUESTRAS[nombre].volumen || 1) * volumen));
    fuente.connect(g).connect(ctx.destination);
    fuente.start();
    return true;
  } catch {
    return false;
  }
}

/** Para las pruebas: olvidar lo cargado y lo preguntado. No lo usa la aplicación. */
export function olvida() {
  cargadas.clear();
  pidiendo.clear();
  rotas.clear();
  preguntado = false;
  ext = undefined;
}

export default { suena, carga, precarga, tiene, lista, formato };
